import React, { useState, useEffect } from 'react';

/**
 * CoreSpinLoader
 * A reusable branded full-screen loading overlay.
 * Used during route transitions (replacing the raw scroll-to-top jump).
 *
 * Props:
 *   visible  — boolean  controls show/hide (fades in/out)
 */
export function CoreSpinLoader({ visible = true }) {
  const [loadingText, setLoadingText] = useState('Loading...');

  // Lock body scroll while loader is visible
  useEffect(() => {
    document.body.style.overflow = visible ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const states = [
      'Loading...',
      'Fetching Data..',
      'Syncing...',
      'Processing..',
      'Optimizing...',
    ];
    let i = 0;
    const interval = setInterval(() => {
      i = (i + 1) % states.length;
      setLoadingText(states[i]);
    }, 900);
    return () => clearInterval(interval);
  }, [visible]);

  return (
    <div
      aria-live="polite"
      aria-label="Loading page"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#ffffff',
        overflow: 'hidden',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'all' : 'none',
        transition: visible ? 'opacity 0.08s ease-out' : 'opacity 0.32s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      {/* Spinner assembly */}
      <div style={{ position: 'relative', width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>

        {/* Base glow */}
        <div style={styles.baseGlow} />

        {/* Outer dashed ring */}
        <div style={styles.outerDashedRing} />

        {/* Main arc (fast CW) */}
        <div style={styles.mainArc} />

        {/* Reverse arc (slower CCW) */}
        <div style={styles.reverseArc} />

        {/* Inner fast ring */}
        <div style={styles.innerRing} />

        {/* Orbital dot wrapper */}
        <div style={styles.orbitalWrapper}>
          <div style={styles.orbitalDot} />
        </div>

        {/* Center core */}
        <div style={styles.centerCore} />
      </div>

      {/* Loading text */}
      <p
        key={loadingText}
        style={styles.loadingText}
      >
        {loadingText}
      </p>

      {/* Keyframes injected inline */}
      <style>{KEYFRAMES}</style>
    </div>
  );
}

/* ─── Inline styles (avoids Tailwind class-name coupling) ─── */
const styles = {
  baseGlow: {
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(76,175,80,0.18) 0%, transparent 70%)',
    animation: 'sipl-loader-pulse 2s ease-in-out infinite',
  },
  outerDashedRing: {
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    border: '1px dashed rgba(76,175,80,0.45)',
    animation: 'sipl-loader-spin 10s linear infinite',
  },
  mainArc: {
    position: 'absolute',
    inset: 4,
    borderRadius: '50%',
    border: '2.5px solid transparent',
    borderTopColor: '#4caf50',
    boxShadow: '0 0 8px rgba(76,175,80,0.55)',
    animation: 'sipl-loader-spin 1.8s linear infinite',
  },
  reverseArc: {
    position: 'absolute',
    inset: 14,
    borderRadius: '50%',
    border: '2px solid transparent',
    borderBottomColor: '#0d9488',
    boxShadow: '0 0 6px rgba(13,148,136,0.45)',
    animation: 'sipl-loader-spin-rev 3s linear infinite',
  },
  innerRing: {
    position: 'absolute',
    inset: 22,
    borderRadius: '50%',
    border: '1.5px solid transparent',
    borderLeftColor: 'rgba(26,47,110,0.7)',
    animation: 'sipl-loader-spin 1s ease-in-out infinite',
  },
  orbitalWrapper: {
    position: 'absolute',
    inset: 0,
    animation: 'sipl-loader-spin 4s linear infinite',
  },
  orbitalDot: {
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: '#4caf50',
    boxShadow: '0 0 6px rgba(76,175,80,0.9)',
  },
  centerCore: {
    position: 'absolute',
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#1a2f6e',
    boxShadow: '0 0 8px rgba(26,47,110,0.6)',
    animation: 'sipl-loader-pulse 1.5s ease-in-out infinite',
  },
  loadingText: {
    marginTop: 28,
    fontSize: '0.65rem',
    fontWeight: 600,
    letterSpacing: '0.3em',
    textTransform: 'uppercase',
    color: 'rgba(26,47,110,0.7)',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    animation: 'sipl-loader-fadein 0.4s ease both',
    margin: '28px 0 0 0',
    padding: 0,
  },
};

const KEYFRAMES = `
  @keyframes sipl-loader-spin     { from { transform: rotate(0deg); }   to { transform: rotate(360deg); } }
  @keyframes sipl-loader-spin-rev { from { transform: rotate(0deg); }   to { transform: rotate(-360deg); } }
  @keyframes sipl-loader-pulse    { 0%,100% { opacity: 0.6; transform: scale(1); } 50% { opacity: 1; transform: scale(1.15); } }
  @keyframes sipl-loader-fadein   { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
`;

export default CoreSpinLoader;
